/** @jsxImportSource @emotion/react */
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import { FloatingInput } from '../../../components/floating-input/floating-input';
import {
  ContinueButton,
  ErrorText,
  FullWidth,
  Headline,
  Line,
  LinkText,
  NewToMailanderWrapper,
  Screen,
  SectionLabel,
  SpinnerWrap,
  Subhead,
} from './email-login.styled';
import { useEmailLogin } from './email-login.hooks';
import { VSpace } from '../../chat/settings/account/account-settings';
import type { LoginStepProps } from '../login-shell.types';
import { useBiometricLogin } from '../biometric-login/biometric-login.hooks';
import { useAuth } from '../../../contexts/auth/auth-context';
import { BiometricsStorage } from '../../../helpers/biometrics-storage';

export const EmailLogin: React.FC<LoginStepProps> = ({ go, setData }) => {
  const navigate = useNavigate();
  const { biometricLogin } = useAuth();

  const biometricsEnabled = BiometricsStorage.isBiometricsEnabled();

  const { email, setEmail, loading, error, submitEmail } = useEmailLogin({
    onEmailSuccess: (email: string) => {
      setData({ email });
      go('password-login');
    },
  });

  const {
    loading: bioLoading,
    error: bioError,
    start: startBiometric,
  } = useBiometricLogin({
    autoStart: false,
    preferConditionalUI: false,
    onSuccess: async (payload) => {
      try {
        await biometricLogin(payload.access_token, payload.refresh_token);
        navigate('/chat', { replace: true });
      } catch {
        // Fall through — user stays on login screen
      }
    },
  });

  const isLoading = loading || bioLoading;

  // Auto-prompt biometrics on session expiry (not manual logout)
  const didAutoStartRef = useRef(false);
  useEffect(() => {
    if (didAutoStartRef.current) return;
    if (!biometricsEnabled) return;

    const isManualLogout = sessionStorage.getItem('manual_logout') === 'true';
    if (isManualLogout) {
      sessionStorage.removeItem('manual_logout');
      return;
    }

    didAutoStartRef.current = true;
    void startBiometric();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Screen data-testid="email-login-screen">
      <div>
        <VSpace $h={32} />
        <Headline>
          Judgment. <br />
          Refined.
        </Headline>

        <VSpace $h={16} />

        <Subhead>The long conversations that create enterprise value.</Subhead>
        <VSpace $h={24} />
      </div>

      <div
        style={{ width: '100%', alignItems: 'center', display: 'flex', flexDirection: 'column' }}
      >
        <SectionLabel>Log in</SectionLabel>

        {/* <FullWidth>
        <GoogleButton onClick={submitGoogle} disabled={loading} data-testid="email-login-google">
          <GoogleIcon src={googleIconSrc} alt="Google" />
          Continue with Google
        </GoogleButton>
      </FullWidth> */}

        {/* <VSpace $h={18} />

      <DividerWrap>
        <Divider>
          <DividerText>or continue with email</DividerText>
        </Divider>
      </DividerWrap> */}

        <VSpace $h={16} />

        <FullWidth>
          <FloatingInput
            id="email"
            type="email"
            label="Enter your email"
            value={email}
            onChange={(e: any) => setEmail(e?.target?.value ?? '')}
            onKeyDown={(e: any) => {
            if (e.key === 'Enter' && !isLoading) {
              e.preventDefault?.();
              void submitEmail();
            }
          }}
          disabled={isLoading}
          />
        </FullWidth>

        {!loading && error && (
          <>
            <VSpace $h={12} />
            <ErrorText data-testid="email-login-error">{error}</ErrorText>
          </>
        )}

        <VSpace $h={16} />

        <FullWidth>
          <ContinueButton
            onClick={submitEmail}
            disabled={isLoading}
            data-testid="email-login-email"
          >
            Continue
          </ContinueButton>

          {loading && (
            <SpinnerWrap data-testid="email-login-loading">
              <UniversalSpinner />
            </SpinnerWrap>
          )}
        </FullWidth>

        <VSpace $h={24} />
        {biometricsEnabled && (
          <>
            <LinkText
              onClick={() => {
                if (!isLoading) void startBiometric();
              }}
              style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
            >
              Log in with Biometrics
            </LinkText>
            {bioLoading && (
              <SpinnerWrap>
                <UniversalSpinner />
              </SpinnerWrap>
            )}
            {!bioLoading && bioError && (
              <>
                <VSpace $h={8} />
                <ErrorText data-testid="biometric-inline-error">{bioError}</ErrorText>
              </>
            )}
          </>
        )}
        <VSpace $h={48} />
      </div>
      <div
        style={{ width: '100%', alignItems: 'center', display: 'flex', flexDirection: 'column' }}
      >
        <NewToMailanderWrapper>
          <Line />
          New to Mailander?
          <Line />
        </NewToMailanderWrapper>
        <VSpace $h={24} />
        <LinkText onClick={() => go('waitlist')}>Request invitation</LinkText>
        <VSpace $h={20} />
        <LinkText
          onClick={() => {
            navigate('/?register');
          }}
        >
          Enter invitation code
        </LinkText>
      </div>
      <div></div>
    </Screen>
  );
};
